import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigation } from '../contexts/NavigationContext';
import { Menu, LogOut, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { organizations } from '../services/api';

const Navigation = () => {
  // Below lg (1024px) start as the icon-only rail so the content area keeps its room
  const [menuOpen, setMenuOpen] = useState(() => window.innerWidth >= 1024);
  const [orgList, setOrgList] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(organizations.getSelectedOrgId() || '');
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { menuItems, userRole } = useNavigation();

  const isSuperAdmin = userRole === 'super_admin';

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setMenuOpen(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      organizations.getAll()
        .then(res => setOrgList(res.data.data || []))
        .catch(err => console.error('Error fetching orgs:', err));
    }
  }, [isSuperAdmin]);

  const handleOrgSwitch = (orgId) => {
    setSelectedOrgId(orgId);
    organizations.switchOrg(orgId || null);
    setOrgDropdownOpen(false);
    // Reload current page to apply new org context
    window.location.reload();
  };

  const selectedOrgName = orgList.find(o => o.id === selectedOrgId)?.name;

  console.log('Navigation: Current user role:', userRole);
  console.log('Navigation: Menu items:', menuItems);
  console.log('Navigation: Current path:', location.pathname);

  const isActive = (href) => {
    // For the dashboard, only match exact path
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    // For other paths, match if the current path starts with the href
    return location.pathname.startsWith(href);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedOrganizationId');
    localStorage.removeItem('organizationId');
    navigate('/login');
  };

  return (
    <div className={`bg-sidebar text-white transition-all duration-300 h-full ${menuOpen ? 'w-64' : 'w-20'} flex flex-col overflow-hidden`}>
      {/* Header */}
      <div className={`p-4 flex items-center flex-shrink-0 ${menuOpen ? 'justify-between' : 'flex-col gap-2'}`}>
        {menuOpen ? (
          <img src="/brand/gia-logo-compact-white.svg" alt="GiA" className="h-9 w-auto" />
        ) : (
          <img src="/brand/gia-mark-white.svg" alt="GiA" className="h-9 w-auto" />
        )}
        <button 
          onClick={() => setMenuOpen(!menuOpen)} 
          className="p-1 rounded hover:bg-sidebar/70"
        >
          <Menu size={20} />
        </button>
      </div>

      {/* User Info */}
      <div className="mt-2 px-4 py-2 flex-shrink-0">
        {menuOpen ? (
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-sidebar/50 flex items-center justify-center text-sm font-medium">
              {userRole?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <div className="text-sm font-medium">{userRole || 'User'}</div>
              <div className="text-xs text-white/70">Role</div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center mb-2">
            <div className="w-10 h-10 rounded-full bg-sidebar/50 flex items-center justify-center text-sm font-medium">
              {userRole?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          </div>
        )}
      </div>

      <div className="border-b border-white/10 mx-4 mb-4 flex-shrink-0"></div>

      {/* Org Switcher (super_admin only) */}
      {isSuperAdmin && menuOpen && (
        <div className="px-4 mb-3 relative flex-shrink-0">
          <button
            onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
            className="w-full flex items-center justify-between px-3 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/15 transition-colors"
          >
            <span className="truncate">{selectedOrgName || 'Todas organizações'}</span>
            <ChevronDown className={`w-4 h-4 ml-1 flex-shrink-0 transition-transform ${orgDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          {orgDropdownOpen && (
            <div className="absolute left-4 right-4 mt-1 bg-white rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
              <button
                onClick={() => handleOrgSwitch('')}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${!selectedOrgId ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-700'}`}
              >
                Todas organizações
              </button>
              {orgList.map(org => (
                <button
                  key={org.id}
                  onClick={() => handleOrgSwitch(org.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${selectedOrgId === org.id ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-700'}`}
                >
                  {org.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Navigation Items */}
      {/* min-h-0 lets the list shrink and scroll on short screens instead of being clipped */}
      <nav className="flex-1 min-h-0 overflow-y-auto px-2">
        {menuItems.map((item) => {
          const active = isActive(item.href);
          return (
            <button
              key={item.name}
              onClick={() => navigate(item.href)}
              title={menuOpen ? undefined : item.name}
              className={`w-full flex items-center ${menuOpen ? '' : 'justify-center'} p-3 mb-2 rounded-lg transition-colors ${
                active ? 'bg-white/10' : 'hover:bg-white/5'
              }`}
            >
              <item.icon size={menuOpen ? 18 : 20} />
              {menuOpen && (
                <div className="ml-3 flex flex-col items-start">
                  <span>{item.name}</span>
                  {item.name === 'GiA chat' && (
                    <span className="text-xs text-white/70">chat</span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </nav>

      {/* Logout Button */}
      <div className="p-4 border-t border-white/10 flex-shrink-0">
        <button
          onClick={handleLogout}
          title={menuOpen ? undefined : 'Logout'}
          className={`w-full flex items-center ${menuOpen ? '' : 'justify-center'} p-2 rounded-lg hover:bg-white/5`}
        >
          <LogOut size={menuOpen ? 18 : 20} />
          {menuOpen && <span className="ml-3">Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default Navigation; 